<template>
  <div ref="mapRoot" class="osm-map"></div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export default {
  name: 'GoogleMap',
  props: {
    items: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      map: null,
      markerLayer: null,
      sovereigntyLayer: null,
      vnBoundaryLayer: null,
      fallbackCenter: [22.1336421, 105.7826459]
    };
  },
  computed: {
    validItems() {
      return (Array.isArray(this.items) ? this.items : [])
        .map((item) => {
          if (!item) return null;
          const lat = Number(item.position && item.position.lat != null ? item.position.lat : item.latitude);
          const lng = Number(item.position && item.position.lng != null ? item.position.lng : item.longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return { ...item, lat, lng };
        })
        .filter(Boolean);
    }
  },
  mounted() {
    if (!this.$hasAuthor('ROLE_ADMIN')) {
      this.$router.replace('/dashboard.html').catch(() => {});
      return;
    }
    this.initMap();
    this.renderMarkers();
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  watch: {
    validItems: {
      deep: true,
      handler() {
        this.renderMarkers();
      }
    }
  },
  methods: {
    initMap() {
      if (this.map) return;
      this.map = L.map(this.$refs.mapRoot, { zoomControl: true }).setView(this.fallbackCenter, 12);
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
        maxZoom: 19,
        subdomains: 'abcd',
        attribution: '&copy; OpenStreetMap &copy; CARTO'
      }).addTo(this.map);
      this.markerLayer = L.layerGroup().addTo(this.map);
      this.sovereigntyLayer = L.layerGroup().addTo(this.map);
      this.renderSovereigntyMarkers();
      this.loadVietnamBoundaryGeoJson();
      setTimeout(() => {
        if (this.map) this.map.invalidateSize();
      }, 100);
    },
    loadVietnamBoundaryGeoJson() {
      if (!this.map || this.vnBoundaryLayer) return;
      fetch('/static/geojson/vietnam.geojson')
        .then((res) => {
          if (!res.ok) throw new Error('Cannot load vietnam.geojson');
          return res.json();
        })
        .then((geojson) => {
          this.vnBoundaryLayer = L.geoJSON(geojson, {
            style: {
              color: '#0b98ff',
              weight: 1.4,
              opacity: 0.9,
              fill: false
            }
          }).addTo(this.map);
        })
        .catch(() => {});
    },
    renderSovereigntyMarkers() {
      if (!this.map || !this.sovereigntyLayer) return;
      this.sovereigntyLayer.clearLayers();
      const sovereigntyPoints = [
        { name: 'Quần đảo Hoàng Sa (Việt Nam)', lat: 16.5, lng: 112.0 },
        { name: 'Quần đảo Trường Sa (Việt Nam)', lat: 9.5, lng: 114.0 }
      ];
      sovereigntyPoints.forEach((p) => {
        const marker = L.circleMarker([p.lat, p.lng], {
          radius: 6,
          color: '#2563eb',
          fillColor: '#0b98ff',
          fillOpacity: 0.95,
          weight: 2
        });
        marker.bindTooltip(p.name, {
          permanent: true,
          direction: 'top',
          offset: [0, -10],
          className: 'sovereignty-label'
        });
        marker.bindPopup(`<b>${p.name}</b>`);
        marker.addTo(this.sovereigntyLayer);
      });
    },
    renderMarkers() {
      if (!this.map || !this.markerLayer) return;
      this.markerLayer.clearLayers();
      if (!this.validItems.length) {
        this.map.setView(this.fallbackCenter, 12);
        return;
      }
      const bounds = [];
      this.validItems.forEach((item) => {
        const color = Number(item.connectStatus) === 1 ? '#0b98ff' : '#ef4444';
        const marker = L.circleMarker([item.lat, item.lng], {
          radius: 7,
          color,
          fillColor: color,
          fillOpacity: 0.9,
          weight: 2
        });
        const safeCode = item.code || '';
        const safeName = item.description || '';
        const directionUrl = `https://www.google.com/maps/dir/?api=1&destination=${item.lat},${item.lng}&travelmode=driving`;
        marker.bindPopup(
          `<div>
            <div><b>Mã thiết bị:</b> ${safeCode}</div>
            <div><b>Tên thiết bị:</b> ${safeName}</div>
            <div><b>Vị trí:</b> ${item.lat}, ${item.lng}</div>
            <div style="margin-top:8px;">
              <a href="${directionUrl}" target="_blank" rel="noopener noreferrer" class="map-direction-btn">Mở chỉ đường</a>
            </div>
          </div>`
        );
        marker.addTo(this.markerLayer);
        bounds.push([item.lat, item.lng]);
      });
      if (bounds.length === 1) {
        this.map.setView(bounds[0], 15);
      } else {
        this.map.fitBounds(bounds, { padding: [24, 24] });
      }
    }
  }
};
</script>

<style scoped>
.osm-map {
  width: 100%;
  height: 70vh;
  min-height: 420px;
  border-radius: 12px;
}

.osm-map >>> .sovereignty-label {
  background: rgba(11, 152, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 8px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.25);
}

.osm-map >>> .map-direction-btn {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 8px;
  background: #0b98ff;
  color: #fff !important;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
}
</style>
